import { message } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import CopyIcon from "../../../assets/icons/purple-copy.svg";
import { TrackingLinkPublicDetailsDto } from "../../../helpers/types";
import { trackingLinksService } from "../../../services/tracking-links.service";
import { copyToClipboard } from "../../../utls/FunctionsUtil";
import ConversationAvatarComponent from "../../ConversationAvatarComponent";
import DateRangePickerModal from "../../DateRangePickerModalComponent/DateRangePickerModalComponent";
import Loader from "../../loader/Loader";
import "./PublicTrackLinkView.less";

const PublicTrackLinkView = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();

  const [trackingLink, setTrackingLink] = useState<TrackingLinkPublicDetailsDto | null>(null);
  const [loading, setLoading] = useState(false);

  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<{ startDate: Date; endDate: Date }>({
    startDate: dayjs().subtract(29, "day").toDate(),
    endDate: dayjs().toDate(),
  });

  useEffect(() => {
    if (!token) return;

    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        const res = await trackingLinksService.getPublicTrackingLinkDetails(token!);

        if (mounted) {
          setTrackingLink(res);
        }
      } catch (e) {
        console.error("Failed to fetch tracking link details", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [token]);

  const items = useMemo(() => {
    if (!trackingLink) return [];

    const start = dayjs(selectedDate.startDate);
    const end = dayjs(selectedDate.endDate);
    const daysCount = end.diff(start, "day") + 1;

    const dataMap = new Map<string, number>();

    for (let i = 0; i < daysCount; i++) {
      const currentDate = start.add(i, "day");
      dataMap.set(currentDate.format("YYYY-MM-DD"), 0);
    }

    if (trackingLink?.clicks?.length) {
      trackingLink.clicks.forEach((click) => {
        const clickDate = dayjs(click.clicksDate);
        if (clickDate.isBetween(start, end, "day", "[]")) {
          const dateKey = clickDate.format("YYYY-MM-DD");
          dataMap.set(dateKey, click.numberOfClicks);
        }
      });
    }

    return Array.from(dataMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, value]) => ({
        label: dayjs(date).format("DD"),
        date: date,
        value,
      }));
  }, [trackingLink, selectedDate]);

  const formatRevenue = (amount: number) => {
    const [integerPart, decimalPart] = amount.toFixed(2).split(".");
    return { integerPart, decimalPart };
  };

  const { integerPart, decimalPart } = formatRevenue(trackingLink?.totalEarnings || 0);

  const handleCopyLink = async () => {
    try {
      await copyToClipboard(trackingLink?.link || "");
      message.success(t("link-copied"));
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };

  if (!trackingLink) return <></>;

  return (
    <div className="public-track-link-view">
      {loading ? (
        <div className="loader-wrapper">
          <Loader size="large" />
        </div>
      ) : (
        <>
          <div className="public-track-link-content">
            {/* Avatar Section */}
            <div className="public-track-link-avatar-section">
              <div className="public-track-link-avatar-border">
                <ConversationAvatarComponent
                  isPublicTL={true}
                  name={trackingLink?.creatorData?.username}
                  avatar={trackingLink?.creatorData?.avatar}
                />
              </div>
            </div>

            {/* Revenue Section */}
            <div className="revenue-section">
              <div className="revenue-amount">
                <span className="revenue-integer">{integerPart},</span>
                <span className="revenue-decimal">{decimalPart}€</span>
              </div>
              <p className="revenue-label">{t("generated-through-this-link")}</p>
            </div>

            {/* Statistics Section */}
            <div className="info-section">
              <h3 className="section-title">{t("statistics")}</h3>
              <div className="info-container">
                <div className="info-row">
                  <span className="info-label">{t("subscription-count")}</span>
                  <span className="info-value">{trackingLink?.totalNumberOfSubscribers}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t("click-count")}</span>
                  <span className="info-value">{trackingLink?.totalNumberOfClicks}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t("total-amount-spent")}</span>
                  <span className="info-value">{trackingLink?.totalEarnings?.toFixed(2).replace(".", ",")}€</span>
                </div>
              </div>
            </div>

            {/* Chart Section */}
            <div className="info-section chart-section">
              <h3 className="section-title">{t("click-count-title")}</h3>
              <div className="chart-container-wrapper">
                <div className="chart-header">
                  <span className="chart-total-clicks">{items?.reduce((acc, item) => acc + item.value, 0).toLocaleString()}</span>
                  <span className="chart-date-range" onClick={() => setOpenDateRangePicker(true)}>
                    {`${t("from")} ${dayjs(selectedDate?.startDate).format("DD/MM/YYYY")} ${t("to")} ${dayjs(selectedDate?.endDate).format("DD/MM/YYYY")}`}
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={items} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="clickGradientPublic" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7FFAA1" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#7FFAA1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" hide={true} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const formattedDate = dayjs(data.date).format("DD MMMM");
                          const clicks = data.value;
                          return (
                            <div
                              style={{
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                padding: "5px 14px",
                                borderRadius: "8px",
                                color: "#FFFFFF",
                              }}
                            >
                              <p style={{ margin: 0, fontSize: "12px", fontWeight: "bold" }}>
                                {clicks} {clicks > 1 ? "clics" : "clic"} le {formattedDate}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#7FFAA1"
                      fill="url(#clickGradientPublic)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: "#7FFAA1" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Public Link Section */}
            <div className="info-section">
              <h3 className="section-title">{t("the-link")}</h3>
              <div className="info-container link-container">
                <span className="affiliate-public-link">{trackingLink?.link}</span>
                <img src={CopyIcon} alt="copy" className="copy-icon" onClick={handleCopyLink} />
              </div>
            </div>
          </div>
          {openDateRangePicker && (
            <DateRangePickerModal
              onClose={() => {
                setOpenDateRangePicker(false);
              }}
              selectedDate={selectedDate}
              onDateSelect={(startDate, endDate) => {
                setSelectedDate({ startDate, endDate });
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PublicTrackLinkView;
